import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, shallowReactive, toRaw, watch } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useState } from '@/composable/useState';
import { assignArray, createMap, remove, upsert } from '@/core/lib/array';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { formatTournamentEvent } from '@/core/oddsApi/helpers/formatEvent';
import type { EnumOddsCategoryType, OddsDisplayEventResultsTypeFragment, PresetFilterInputType, TournamentEventTypeFragment } from '@/core/oddsApi/oddsApiType';
import { EnumTimeZoneType, useEventResultsQuery, useEventResultsSubscription, useTournamentEventsQuery, useTournamentEventsSubscription } from '@/core/oddsApi/oddsApiType';
import { SportType } from '@/interface/enum';
import type { ITournamentEvent } from '@/interface/tournamentCenter';
import { useBehaviorStore } from '@/store/behaviorStore';
import { useOddsApiStore } from '@/store/oddsApiStore';

interface IUseTournamentEventsParam {
    sportType: MaybeRef<SportType>;
    presetFilter: MaybeRef<PresetFilterInputType>;
    oddsCategory: MaybeRef<EnumOddsCategoryType>;
    eventIds?: MaybeRef<number[] | null>;
    leagueIds?: MaybeRef<number[] | null>;
    leagueNames?: MaybeRef<string[] | null>;
    isActive?: MaybeRef<boolean>;
    isResetResults?: MaybeRef<boolean>;
}

export function useTournamentEvents({
    sportType,
    oddsCategory,
    presetFilter,
    eventIds,
    leagueIds,
    leagueNames,
    isActive = true,
    isResetResults = true,
}: IUseTournamentEventsParam) {
    const variables = computed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            filter: get(presetFilter),
            oddsCategory: get(oddsCategory),
            eventIds: get(eventIds),
            tournamentIds: get(leagueIds),
            tournamentNames: get(leagueNames),
            timeZone: EnumTimeZoneType.UTC__4,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const _rawEvents = shallowReactive<TournamentEventTypeFragment[]>([]);
    const _rawEventResults = shallowReactive<OddsDisplayEventResultsTypeFragment[]>([]);

    const events = ref<ITournamentEvent[]>([]);

    watch([_rawEvents, _rawEventResults], () => {
        const rawEventResults = toRaw(_rawEventResults);
        const rawEvents = toRaw(_rawEvents);
        const eventResultsCache = createMap(
            rawEventResults,
            x => x.id,
            x => x.eventResults,
        );
        const newEvents = rawEvents.reduce<ITournamentEvent[]>(
            (results, event) => {
                const eventResults = eventResultsCache.get(event.id);
                results.push(deepFreeze(formatTournamentEvent(event, eventResults ?? [])));
                return results;
            },
            [],
        );

        assignArray(events.value, newEvents, a => a.id);
    });

    const {
        loaded: eventsLoaded,
        loading: eventsLoading,
        error: eventsError,
        onResult,
    } = useTournamentEventsQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive) && isEnabled.value,
        })),
    );

    const { onSubscription } = useTournamentEventsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive),
        })),
    );

    const {
        loaded: eventResultsLoaded,
        loading: eventResultsLoading,
        onResult: onEventResultsResult,
    } = useEventResultsQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive) && isEnabled.value,
        })),
    );

    const { onSubscription: onEventResultSubscription } = useEventResultsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive),
        })),
    );

    const { addEventsQueryCount } = useBehaviorStore();
    const { customerSettings, additionalSettings } = storeToRefs(
        useOddsApiStore(),
    );
    onResult((result) => {
        if (result) {
            assignArray(
                _rawEvents,
                result.filter(x => BusinessLogicHelper.isEventAllowed(
                    customerSettings.value,
                    x,
                    additionalSettings.value,
                )),
                item => item.id,
            );
        }
        addEventsQueryCount();
    });

    onEventResultsResult((result) => {
        if (result) {
            assignArray(
                _rawEventResults,
                result.filter(x => BusinessLogicHelper.isEventAllowed(
                    customerSettings.value,
                    x,
                    additionalSettings.value,
                )),
                item => item.id,
            );
        }
    });

    onSubscription((subscription) => {
        upsert(
            _rawEvents,
            subscription?.updated.filter(x => BusinessLogicHelper.isEventAllowed(
                customerSettings.value,
                x,
                additionalSettings.value,
            )) ?? [],
            item => item.id,
        );
        remove(_rawEvents, subscription?.deleted ?? [], (a, b) => a.id === b);
    });

    onEventResultSubscription((subscription) => {
        upsert(
            _rawEventResults,
            subscription?.updated.filter(x => BusinessLogicHelper.isEventAllowed(
                customerSettings.value,
                x,
                additionalSettings.value,
            )) ?? [],
            item => item.id,
        );
        remove(_rawEventResults, subscription?.deleted ?? [], (a, b) => a.id === b);
    });

    watch(variables, () => {
        if (get(isResetResults)) {
            eventsLoaded.value = false;
            eventResultsLoaded.value = false;
            _rawEvents.length = 0;
            _rawEventResults.length = 0;
        }
    });

    const loaded = computed(() => eventsLoaded.value && eventResultsLoaded.value);
    const loading = computed(
        () => eventsLoading.value || eventResultsLoading.value,
    );

    return {
        events,
        loaded,
        loading,
        eventsError,
        refetch,
    };
}
